import { useMemo } from "react";
import type { Translation } from "../../../../hooks/useTranslations";
import type { LockCalculatorDefinition, Result } from "../../../../http/lock_calculator/types";
import type { LockCalculatorFormValues } from "../model/form";

export type VerificationCard = { title: string; symbol: string; load: number | null; resistance: number | null };

/** Builds the per-axis verification cards for any Configuration's Result, given the (Definition-level, shared) loads. */
export const buildVerificationCards = (
  loads: LockCalculatorFormValues["loads"] | undefined,
  result: Result | null | undefined,
  t: Translation,
): VerificationCard[] => [
  { title: t.lockCalculatorModule.results.axes.vertical, symbol: "↕", load: loads?.vertical ?? null, resistance: result?.fvD ?? null },
  { title: t.lockCalculatorModule.results.axes.uplift, symbol: "⇡", load: loads?.uplift ?? null, resistance: result?.fupD ?? null },
  { title: t.lockCalculatorModule.results.axes.lateral, symbol: "⇆", load: loads?.lateral ?? null, resistance: result?.flatD ?? null },
  { title: t.lockCalculatorModule.results.axes.axial, symbol: "↔", load: loads?.axial ?? null, resistance: result?.faxD ?? null },
];

/** Builds the presentation read model from form loads and backend resistances. */
export const useLockCalculatorResults = (
  values: Partial<LockCalculatorFormValues> | undefined,
  calculation: LockCalculatorDefinition | null,
  t: Translation,
) => {
  const selectedAxesCount = [
    values?.combinedVerification?.includeVertical,
    values?.combinedVerification?.includeUplift,
    values?.combinedVerification?.includeAxial,
    values?.combinedVerification?.includeLateral,
  ].filter(Boolean).length;
  const hasActiveCombination = selectedAxesCount >= 2;
  const result = calculation?.configuration?.result;
  const hasResults = Boolean(
    result &&
      (result.fvD != null || result.fupD != null || result.faxD != null || result.flatD != null ||
        (result.etaCombined != null && hasActiveCombination)),
  );
  const verificationCards = useMemo<VerificationCard[]>(
    () => buildVerificationCards(values?.loads, result, t),
    [result?.faxD, result?.flatD, result?.fupD, result?.fvD, t, values?.loads?.axial, values?.loads?.lateral, values?.loads?.uplift, values?.loads?.vertical],
  );
  return { hasActiveCombination, hasResults, verificationCards };
};
